import React, { useEffect, useState, useContext, useCallback } from "react";
import { AuthContext } from "utils/AuthContext";
import { FlexLeftCol } from "styles/global-style";
import Input from "components/Input";
import Textarea from "components/Textarea";
import Button from "components/Button";
import Modal from "components/Modal";
import { getDefaultProjectData, getDoDsByDiscipline } from "utils/DefaultProjectData";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export default function DoDs() {
  const { supabase, user } = useContext(AuthContext);
  const [disciplines, setDisciplines] = useState([]);
  const [doDsByDiscipline, setDoDsByDiscipline] = useState({});
  
  // Helper to update disciplines and doDsByDiscipline together
  const updateDisciplines = useCallback((newDisciplines) => {
    setDisciplines(newDisciplines);
    const grouped = getDoDsByDiscipline(newDisciplines);
    setDoDsByDiscipline(grouped);
  }, []);
  const [editingDoD, setEditingDoD] = useState(null);
  const [editingDisciplineId, setEditingDisciplineId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [addingToDisciplineId, setAddingToDisciplineId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  // Load disciplines with DoDs
  const loadDisciplines = useCallback(async () => {
    if (!supabase || !user?.proj?.id) return;

    const projectType = user.proj.type;
    if (!projectType) {
      setDisciplines([]);
      setDoDsByDiscipline({});
      return;
    }

    const defaultDisciplines = getDefaultProjectData(projectType);

    const { data: setting } = await supabase
      .from("settings")
      .select("value")
      .eq("project_id", user.proj.id)
      .eq("name", "project_disciplines")
      .maybeSingle();

    if (setting?.value) {
      try {
        const customDisciplines = JSON.parse(setting.value);
        // Merge: use custom disciplines, fill in missing properties from defaults
        const defaultMap = new Map(defaultDisciplines.map(d => [d.id, d]));
        
        const merged = customDisciplines.map(customDiscipline => {
          const defaultDiscipline = defaultMap.get(customDiscipline.id);
          if (defaultDiscipline) {
            // Merge discipline properties, but use custom DoDs if they exist
            return {
              ...defaultDiscipline,
              ...customDiscipline,
              // If custom has doDs, use them; otherwise use defaults
              doDs: customDiscipline.doDs && customDiscipline.doDs.length > 0 
                ? customDiscipline.doDs 
                : (defaultDiscipline.doDs || []),
              // Preserve excludeFromDoDs property, default to false if not set
              excludeFromDoDs: customDiscipline.excludeFromDoDs === true
            };
          }
          return {
            ...customDiscipline,
            excludeFromDoDs: customDiscipline.excludeFromDoDs === true
          };
        });
        
        // Sort by order
        merged.sort((a, b) => (a.order || 0) - (b.order || 0));
        updateDisciplines(merged);
      } catch (e) {
        console.error("Failed to parse project_disciplines setting:", e);
        updateDisciplines(defaultDisciplines);
      }
    } else {
      updateDisciplines(defaultDisciplines);
    }
  }, [supabase, user, updateDisciplines]);

  // Initial load
  useEffect(() => {
    loadDisciplines();
  }, [loadDisciplines]);

  // Save disciplines (with DoDs) to settings
  // Only saves if there are actual customizations, not just defaults
  // Note: UI is updated optimistically in onDragEnd, so this just updates the ref
  const saveDisciplines = useCallback(async (newDisciplines) => {
    if (!supabase || !user?.proj?.id) return;

    const projectType = user.proj.type;
    const defaultDisciplines = getDefaultProjectData(projectType);
    const defaultMap = new Map(defaultDisciplines.map(d => [d.id, d]));

    // Check if result is just defaults (no customizations)
    const hasCustomizations = newDisciplines.some(discipline => {
      const defaultDiscipline = defaultMap.get(discipline.id);
      if (!defaultDiscipline) return true; // Custom discipline not in defaults
      
      // Check if discipline properties differ
      if (discipline.name !== defaultDiscipline.name ||
          discipline.color !== defaultDiscipline.color ||
          discipline.order !== defaultDiscipline.order ||
          discipline.excludeFromDoDs === true) {
        return true;
      }

      // Check if DoDs differ (including order)
      const defaultDoDs = defaultDiscipline.doDs || [];
      const customDoDs = discipline.doDs || [];
      
      if (defaultDoDs.length !== customDoDs.length) return true;
      
      // Check if the sequence of DoD IDs differs (reordering)
      const defaultIds = defaultDoDs.map(d => d.id).join(',');
      const customIds = customDoDs.map(d => d.id).join(',');
      if (defaultIds !== customIds) return true;
      
      // Check if content differs (same order, but content changed)
      return customDoDs.some((customDoD, idx) => {
        const defaultDoD = defaultDoDs[idx];
        if (!defaultDoD) return true;
        return customDoD.name !== defaultDoD.name ||
               customDoD.description !== defaultDoD.description;
      });
    });

    if (hasCustomizations) {
      // Store disciplines with their DoDs if they differ from defaults
      const toStore = newDisciplines.map((discipline) => {
        const defaultDiscipline = defaultMap.get(discipline.id);
        const defaultDoDs = defaultDiscipline?.doDs || [];
        const customDoDs = discipline.doDs || [];
        
        // Only store doDs if they differ from defaults (including order)
        let doDsDiffer = !defaultDoDs || defaultDoDs.length !== customDoDs.length;
        
        if (!doDsDiffer && customDoDs.length > 0) {
          // Check if sequence of IDs differs (reordering)
          const defaultIds = defaultDoDs.map(d => d.id).join(',');
          const customIds = customDoDs.map(d => d.id).join(',');
          if (defaultIds !== customIds) {
            doDsDiffer = true; // Order changed
          } else {
            // Check if content differs
            doDsDiffer = customDoDs.some((dod, idx) => {
              const defaultDoD = defaultDoDs[idx];
              return !defaultDoD || 
                     dod.name !== defaultDoD.name || 
                     dod.description !== defaultDoD.description;
            });
          }
        }
        
        // Build the discipline object to store
        const { doDs, ...disciplineWithoutDoDs } = discipline;
        // Always include excludeFromDoDs if it's true (or explicitly false if it was set)
        const result = { ...disciplineWithoutDoDs };
        if (discipline.excludeFromDoDs === true) {
          result.excludeFromDoDs = true;
        }
        if (doDsDiffer && customDoDs.length > 0) {
          // Include the full DoDs array with the reordered sequence
          return { ...result, doDs: [...customDoDs] };
        }
        return result;
      });

      // First, delete ALL existing project_disciplines rows for this project
      await supabase
        .from("settings")
        .delete()
        .eq("project_id", user.proj.id)
        .eq("name", "project_disciplines");

      // Then insert a single row
      await supabase
        .from("settings")
        .insert({
          name: "project_disciplines",
          value: JSON.stringify(toStore),
          project_id: user.proj.id
        });
    } else {
      // Result is just defaults, so delete the setting
      await supabase
        .from("settings")
        .delete()
        .eq("project_id", user.proj.id)
        .eq("name", "project_disciplines");
    }

    // Note: UI is updated optimistically in onDragEnd, so this just ensures ref stays in sync
    // Actually, we don't need to do anything here since onDragEnd already updated state
  }, [supabase, user]);

  // Handle drag end - reorder DoDs within a discipline
  // Uses optimistic update pattern (like Work.js): update UI immediately, then save to server
  const onDragEnd = useCallback(async (result) => {
    if (!result.destination) return;

    // Prevent dragging between different droppables
    if (result.source.droppableId !== result.destination.droppableId) return;

    const disciplineName = result.destination.droppableId.replace("discipline_", "");
    
    // Use functional update to get current disciplines
    setDisciplines(currentDisciplines => {
      const discipline = currentDisciplines.find(d => d.name === disciplineName);
      if (!discipline) return currentDisciplines;

      const disciplineDoDs = [...(discipline.doDs || [])];
      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;
      
      // Reorder - array order is the source of truth
      const [reorderedItem] = disciplineDoDs.splice(sourceIndex, 1);
      disciplineDoDs.splice(destIndex, 0, reorderedItem);

      // Update the discipline's DoDs with reordered array
      const updated = currentDisciplines.map(d => 
        d.id === discipline.id 
          ? { ...d, doDs: [...disciplineDoDs] }
          : d
      );

      // Optimistic update: Update doDsByDiscipline immediately (drag library handles visual transition)
      const grouped = getDoDsByDiscipline(updated);
      setDoDsByDiscipline(grouped);
      
      // Save to server (if error, reload to revert optimistic update)
      saveDisciplines(updated).catch(error => {
        console.error("Failed to save DoD reorder:", error);
        loadDisciplines(); // Revert by reloading from server
      });
      
      return updated;
    });
  }, [saveDisciplines, loadDisciplines]);

  // Start editing
  const startEdit = useCallback((dod, disciplineId) => {
    setEditingDoD(dod);
    setEditingDisciplineId(disciplineId);
    setEditName(dod.name);
    setEditDescription(dod.description);
  }, []);

  // Start adding
  const startAdd = useCallback((disciplineId) => {
    setIsAdding(true);
    setAddingToDisciplineId(disciplineId);
    setEditName("");
    setEditDescription("");
  }, []);

  // Save edit/add
  const saveEdit = useCallback(async () => {
    if (!editName.trim()) {
      alert("Name is required");
      return;
    }

    const disciplineId = editingDisciplineId || addingToDisciplineId;
    
    setDisciplines(currentDisciplines => {
      const discipline = currentDisciplines.find(d => d.id === disciplineId);
      if (!discipline) return currentDisciplines;

      const newDoD = {
        id: editingDoD ? editingDoD.id : `custom_${Date.now()}`,
        name: editName.trim(),
        description: editDescription.trim()
      };

      const updated = currentDisciplines.map(d => {
      if (d.id === disciplineId) {
        const doDs = d.doDs || [];
    if (editingDoD) {
      // Update existing
          return {
            ...d,
            doDs: doDs.map(dod => dod.id === editingDoD.id ? newDoD : dod)
          };
    } else {
      // Add new
          return {
            ...d,
            doDs: [...doDs, newDoD]
          };
        }
      }
      return d;
      });

      // Optimistic update: Update doDsByDiscipline immediately
      const grouped = getDoDsByDiscipline(updated);
      setDoDsByDiscipline(grouped);
      
      // Save to server (if error, reload to revert)
      saveDisciplines(updated).catch(error => {
        console.error("Failed to save DoD:", error);
        loadDisciplines();
      });
      
      return updated;
    });
    
    setEditingDoD(null);
    setEditingDisciplineId(null);
    setIsAdding(false);
    setAddingToDisciplineId(null);
  }, [editingDoD, editingDisciplineId, addingToDisciplineId, editName, editDescription, saveDisciplines, loadDisciplines]);

  // Delete DoD
  const deleteDoD = useCallback(async (dod, disciplineId) => {
    if (!window.confirm(`Are you sure you want to delete "${dod.name}"?`)) return;

    setDisciplines(currentDisciplines => {
      const updated = currentDisciplines.map(d => {
        if (d.id === disciplineId) {
          return {
            ...d,
            doDs: (d.doDs || []).filter(dodItem => dodItem.id !== dod.id)
          };
        }
        return d;
      });

      // Optimistic update: Update doDsByDiscipline immediately
      const grouped = getDoDsByDiscipline(updated);
      setDoDsByDiscipline(grouped);
      
      // Save to server (if error, reload to revert)
      saveDisciplines(updated).catch(error => {
        console.error("Failed to delete DoD:", error);
        loadDisciplines();
      });
      
      return updated;
    });
  }, [saveDisciplines, loadDisciplines]);


  return (
    <FlexLeftCol>
      <h2>Definitions of Done</h2>
      <p style={{ color: "#666", marginBottom: 20 }}>
        Manage the Definition of Done states for subepics. Drag and drop to reorder.
      </p>

      <DragDropContext onDragEnd={onDragEnd}>
        {disciplines.length > 0 ? (
          // Show all disciplines, even if they don't have DoDs yet
          disciplines.map(discipline => {
            const disciplineDoDs = doDsByDiscipline[discipline.name] || [];
            
            return (
              <div key={discipline.id} style={{ marginBottom: 32 }}>
                <div style={{ 
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 12
                }}>
                  <div style={{ 
                    fontSize: 18, 
                    fontWeight: "bold", 
                    padding: "8px 12px",
                    background: discipline.color || "#f0f0f0",
                    color: "white",
                    borderRadius: 5,
                    display: "inline-block"
                  }}>
                    {discipline.name}
                  </div>
                  <label style={{ 
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 14,
                    color: "#666",
                    cursor: "pointer",
                    userSelect: "none"
                  }}>
                    <input
                      type="checkbox"
                      checked={discipline.excludeFromDoDs === true}
                      onChange={(e) => {
                        setDisciplines(currentDisciplines => {
                          const updated = currentDisciplines.map(d => 
                            d.id === discipline.id 
                              ? { ...d, excludeFromDoDs: e.target.checked }
                              : d
                          );
                          
                          // Save to server
                          saveDisciplines(updated).catch(error => {
                            console.error("Failed to save excludeFromDoDs setting:", error);
                            loadDisciplines();
                          });
                          
                          return updated;
                        });
                      }}
                      style={{ cursor: "pointer" }}
                    />
                    <span>Exclude from DoDs</span>
                  </label>
                </div>
                <Droppable droppableId={`discipline_${discipline.name}`} type={`discipline_${discipline.name}`}>
                  {(provided, snapshot) => (
                    <div 
                      {...provided.droppableProps} 
                      ref={provided.innerRef}
                      style={{
                        minHeight: snapshot.isDraggingOver ? 50 : 0,
                        transition: 'min-height 0.2s ease'
                      }}
                    >
                      {disciplineDoDs.map((dod, dodIndex) => (
                        <Draggable 
                          key={dod.id} 
                          draggableId={`${discipline.id}_${dod.id}`} 
                          index={dodIndex}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                marginBottom: 12,
                                padding: 16,
                                background: snapshot.isDragging ? "#f0f0f0" : "#fff",
                                border: "1px solid #ddd",
                                borderRadius: 8,
                                boxShadow: snapshot.isDragging ? "0 4px 8px rgba(0,0,0,0.2)" : "0 1px 3px rgba(0,0,0,0.1)",
                                // Ensure proper z-index when dragging
                                ...(snapshot.isDragging && { zIndex: 1000 })
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                                    <div {...provided.dragHandleProps} style={{ cursor: "grab", fontSize: 18, color: "#999" }}>
                                      ⋮⋮
                                    </div>
                                    <div>
                                      <strong style={{ fontSize: 16, color: "#111" }}>{dod.name}</strong>
                                    </div>
                                  </div>
                                  <div style={{ fontSize: 14, color: "#444", whiteSpace: "pre-line", marginLeft: 30 }}>
                                    {dod.description}
                                  </div>
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <button
                                    onClick={() => startEdit(dod, discipline.id)}
                                    style={{
                                      padding: "6px 12px",
                                      background: "#27ae60",
                                      color: "white",
                                      border: "none",
                                      borderRadius: 4,
                                      cursor: "pointer",
                                      fontSize: 12,
                                      fontWeight: "bold"
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => deleteDoD(dod, discipline.id)}
                                    style={{
                                      padding: "6px 12px",
                                      background: "#e74c3c",
                                      color: "white",
                                      border: "none",
                                      borderRadius: 4,
                                      cursor: "pointer",
                                      fontSize: 12,
                                      fontWeight: "bold"
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      <div style={{ marginTop: 12 }}>
                        <Button 
                          text="Add DoD" 
                          callback={() => startAdd(discipline.id)} 
                        />
                      </div>
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })
        ) : (
          <div style={{ padding: 20, color: "#666" }}>
            No disciplines found. Please configure disciplines first.
              </div>
        )}
      </DragDropContext>

      {/* Edit/Add Modal */}
      {(editingDoD || isAdding) && (
        <Modal
          title={editingDoD ? "Edit DoD" : "Add DoD"}
          closeCb={() => {
            setEditingDoD(null);
            setEditingDisciplineId(null);
            setIsAdding(false);
            setAddingToDisciplineId(null);
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 400 }}>
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="DoD Name"
              autoFocus
              left
              style={{ fontWeight: "normal", textAlign: "left" }}
            />
            <Textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Description..."
              white
              customStyle={{
                color: "#111",
                border: "1px solid #ddd",
                fontFamily: "inherit",
                fontSize: 14,
                fontWeight: "normal",
                background: "#fff",
                textAlign: "left",
                boxShadow: "none",
                outline: "none",
                padding: 12,
                margin: 0,
                resize: "vertical",
                minHeight: 100
              }}
            />
            <div style={{ display: "flex", gap: 12, marginTop: 8, justifyContent: "flex-end" }}>
              <button
                style={{ padding: "8px 18px", borderRadius: 5, border: "none", background: "#27ae60", color: "white", fontWeight: "bold", cursor: "pointer" }}
                onClick={saveEdit}
              >
                Save
              </button>
              <button
                style={{ padding: "8px 18px", borderRadius: 5, border: "none", background: "#bbb", color: "white", fontWeight: "bold", cursor: "pointer" }}
                onClick={() => {
                  setEditingDoD(null);
                  setEditingDisciplineId(null);
                  setIsAdding(false);
                  setAddingToDisciplineId(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </FlexLeftCol>
  );
}
